import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useSnackbar } from 'notistack';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Form from '@gef-ui/form-material/Form';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { useDefaultRedirect } from 'app/hooks';
import { codelistProvider } from 'components';
import Panel from 'components/atoms/GefMui/Panel/Panel';
import Subheader from 'components/atoms/GefMui/Subheader';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import taskDetail from 'features/views/taskDetail';
import { setViewedState } from 'features/views/taskDetail/actions';
import { HAS_NO_ACCESS_ERR_MSG } from 'features/views/taskDetail/constants';
import formDescriptorFn, { TASK_DETAIL_FORM } from 'features/views/taskDetail/form';
import { getCodelistPropById } from 'utils';
import LeadTaskHistoryModal from 'pages/LeadTaskHistoryModal/modals/LeadTaskHistoryModal';

import AdditionalInformationPS from './components/AdditionalInformationPS';
import Attachments from './components/Attachments';
import ClientInfoPrintOnly from './components/ClientInfoPrintOnly';
import Feedback from './components/Feedback';
import Informations from './components/Informations';
import TaskDetailNotes from './components/Notes';
import PolicyAdminsTable from './components/PolicyAdminsTable';
import PolicyInfoPrintOnly from './components/PolicyInfoPrintOnly';
import SubheaderToolbar from './components/SubheaderToolbar';
import TaskGrid from './components/TaskGrid';
import TaskCopyEmailModal from './modals/TaskCopyEmailModal';
import TaskPszpNoticeModal from './modals/TaskPszpNoticeModal';
import TaskForwardingModal from './modals/TasksForwarding/TaskForwardingModal';

const { urls } = fetch.constants;

export const TaskDetail = ({ taskId, read, types }) => {
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const defaultRedirectIfFetchFails = useDefaultRedirect();
	const navigate = useNavigate();
	const { state } = useLocation();
	const locationFrom = state?.from;

	const typeId = useSelector(taskDetail.selectors.getTypeId);

	useEffect(() => {
		if (!read) {
			dispatch(setViewedState(taskId));
		} else {
			dispatch(taskDetail.actions.fetchDetails(taskId));
			dispatch(taskDetail.actions.fetchAttachments(taskId));
		}
	}, [dispatch, taskId, read]);

	const { status: fetchDetailErrorStatus = null, parsedContent: fetchDetailParsedContent = {} } = useSelector(
		fetch.selectors.getMetaData(urls.taskDetail),
	);

	const { status: openDetailErrorStatus = null, parsedContent: openDetailParsedContent = {} } = useSelector(
		fetch.selectors.getMetaData(`${urls.taskOpen}.${taskId}`),
	);

	const handleRedirectAndReloadIfFetchFails = useCallback(() => {
		if (locationFrom) {
			// taskSearch, tasksOverview
			navigate(-1);
		} else {
			// direct url to detail (from GPK etc)
			navigate(defaultRedirectIfFetchFails);
		}

		enqueueSnackbar(getLabelMessage('toastr:taskDetailErrorUserNotResolver'), { variant: 'error' });
	}, [navigate, enqueueSnackbar, locationFrom, defaultRedirectIfFetchFails]);

	useEffect(() => {
		if (fetchDetailErrorStatus === 403 && includes(HAS_NO_ACCESS_ERR_MSG, fetchDetailParsedContent?.message)) {
			handleRedirectAndReloadIfFetchFails();
		}

		if (openDetailErrorStatus === 403 && includes(HAS_NO_ACCESS_ERR_MSG, openDetailParsedContent?.message)) {
			handleRedirectAndReloadIfFetchFails();
		}
	}, [
		fetchDetailErrorStatus,
		fetchDetailParsedContent?.message,
		openDetailErrorStatus,
		openDetailParsedContent?.message,
		handleRedirectAndReloadIfFetchFails,
	]);

	const handleReloadTaskDetail = useCallback(() => {
		dispatch(taskDetail.actions.fetchDetails(taskId));
	}, [dispatch, taskId]);

	const handleReloadTaskAttachments = useCallback(() => {
		dispatch(taskDetail.actions.fetchAttachments(taskId));
	}, [dispatch, taskId]);

	const taskDescription = getCodelistPropById(typeId, types);
	const info = getCodelistPropById(typeId, types, 'informative');

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: taskDescription || taskId,
		backButton: true,
	});

	return (
		<Panel screenId={PAGES.taskDetail.id} screenName={PAGES.taskDetail.name}>
			<LeadTaskHistoryModal id={MODALS.lead_task_history} />
			<TaskCopyEmailModal id={MODALS.task_copy_email} />
			<TaskPszpNoticeModal id={MODALS.task_pszp_notice} />
			<TaskForwardingModal id={MODALS.task_forwarding} />
			<PolicyInfoPrintOnly />
			<ClientInfoPrintOnly />
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<Subheader title={subheaderTitle} headerToolbar={<SubheaderToolbar taskId={taskId} />} />
					<Stack spacing={4}>
						{info === 0 && <Feedback onReload={handleReloadTaskDetail} />}
						<Form name={TASK_DETAIL_FORM} formDescriptorFn={formDescriptorFn}>
							<TaskDetailNotes taskId={taskId} onReload={handleReloadTaskDetail} />
						</Form>
					</Stack>
				</Stack>
			</Paper>
			<TaskGrid onReload={handleReloadTaskDetail} />
			{includes(typeId, [17]) && <Informations onReload={handleReloadTaskDetail} />}
			{includes(typeId, [17, 20]) && <AdditionalInformationPS onReload={handleReloadTaskDetail} />}
			<PolicyAdminsTable onReloadTaskDetail={handleReloadTaskDetail} />
			<Attachments taskId={taskId} onReload={handleReloadTaskAttachments} />
		</Panel>
	);
};

TaskDetail.propTypes = {
	read: PropTypes.bool,
	taskId: PropTypes.string,
	types: PropTypes.array,
};

export default codelistProvider(codelists.taskTypes, 'types')(TaskDetail);
